import { Inject, Injectable } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { Breadcrumbs, NavigationOption } from '@vendasta/uikit';
import { Observable, combineLatest, BehaviorSubject } from 'rxjs';
import { filter, map, shareReplay, startWith } from 'rxjs/operators';
import { MeetingSchedulerServiceModule } from '../data-providers/meeting-scheduler-service.module';
import {
  CALENDAR_PAGE_LINK_TOKEN,
  EVENT_TYPE_PAGE_TOKEN,
  GROUP_PAGE_TOKEN,
  SERVICE_PAGE_TOKEN,
  INTEGRATIONS_PAGE_LINK_TOKEN,
  MEETING_LIST_PAGE_TOKEN,
  PERSONAL_CALENDAR_ID_TOKEN,
  SETTINGS_LINK_TOKEN,
  CALENDAR_VIEW_PAGE_LINK_TOKEN,
  MEETING_TYPE_LIST_VIEW_PAGE_TOKEN, MEETING_SETTINGS_LIST_VIEW_PAGE_TOKEN,
} from '../data-providers/providers';
import { MeetingSchedulerNavActionButton, MeetingSchedulerNavigationButtonType } from '../interface';
import { CALENDAR_ID_PREFIX, GROUP_ID_PREFIX, SERVICE_ID_PREFIX } from '../constants';

export interface BreadcrumbsWithActions {
  breadcrumbs: Breadcrumbs[];
  actions: MeetingSchedulerNavActionButton[];
}

// Not provided in lazy-loading because the translations for the breadcrumbs do not work correctly otherwise
@Injectable({
  providedIn: MeetingSchedulerServiceModule,
})
export class MeetingSchedulerNavigationBreadcrumbService {
  public readonly breadcrumbs$: Observable<Breadcrumbs[] | null>;
  public readonly actions$: Observable<MeetingSchedulerNavActionButton[] | null>;
  public readonly hideBackNavigation$ = new BehaviorSubject<boolean>(false);
  public readonly displayMyMeetingsActions$ = new BehaviorSubject<boolean>(false);

  private readonly breadcrumbsWithActions$: Observable<BreadcrumbsWithActions | null>;

  constructor(
    private readonly router: Router,
    @Inject(SETTINGS_LINK_TOKEN) private readonly settingsLink$: Observable<string>,
    @Inject(INTEGRATIONS_PAGE_LINK_TOKEN) private readonly integrationsPageLink$: Observable<string>,
    @Inject(CALENDAR_PAGE_LINK_TOKEN) private readonly calendarPage$: Observable<string>,
    @Inject(EVENT_TYPE_PAGE_TOKEN) private readonly eventTypePage$: Observable<string>,
    @Inject(GROUP_PAGE_TOKEN) private readonly groupPage$: Observable<string>,
    @Inject(SERVICE_PAGE_TOKEN) private readonly servicePage$: Observable<string>,
    @Inject(MEETING_LIST_PAGE_TOKEN) private readonly meetingListPage$: Observable<string>,
    @Inject(PERSONAL_CALENDAR_ID_TOKEN) public readonly personalCalendarId$: Observable<string>,
    @Inject(CALENDAR_VIEW_PAGE_LINK_TOKEN) public readonly myMeetingPageLinkToken$: Observable<string>,
    @Inject(MEETING_TYPE_LIST_VIEW_PAGE_TOKEN) private readonly meetingTypeListViewPage$: Observable<string>,
    @Inject(MEETING_SETTINGS_LIST_VIEW_PAGE_TOKEN) private readonly meetingSettingsListViewPage$: Observable<string>,
    private readonly translate: TranslateService,
  ) {
    // Helper function to extract query parameters from URL
    const getQueryParam = (url: string, param: string): string | null => {
      const urlParts = url.split('?');
      if (urlParts.length < 2) return null;
      const queryString = urlParts[1];
      const params = new URLSearchParams(queryString);
      return params.get(param);
    };

    // Helper function to get appropriate breadcrumb translation key
    const getBreadcrumbKey = (url: string, isUpdate = false): string => {
      const calendarType = getQueryParam(url, 'calendar_type');
      const showScheduleAccordion = getQueryParam(url, 'show_schedule_accordion');
      if (calendarType === 'session') {
        // Check if this is a schedule flow
        if (showScheduleAccordion === 'true') {
          return 'MEETING_SCHEDULER.BOOKING_LINK.BREADCRUMB_SESSION_SCHEDULE';
        }
        return isUpdate
          ? 'MEETING_SCHEDULER.BOOKING_LINK.BREADCRUMB_SESSION_UPDATE'
          : 'MEETING_SCHEDULER.BOOKING_LINK.BREADCRUMB_SESSION';
      }
      return isUpdate
        ? 'MEETING_SCHEDULER.BOOKING_LINK.BREADCRUMB_UPDATE'
        : 'MEETING_SCHEDULER.BOOKING_LINK.BREADCRUMB';
    };

    const links$ = combineLatest([
      this.settingsLink$,
      this.integrationsPageLink$,
      this.calendarPage$,
      this.eventTypePage$,
      this.meetingListPage$,
      this.groupPage$,
      this.servicePage$,
      this.myMeetingPageLinkToken$,
      this.meetingTypeListViewPage$,
      this.meetingSettingsListViewPage$,
    ]).pipe(
      map(
        ([
          settings,
          integration,
          calendarPage,
          eventTypePage,
          meetingList,
          groupPage,
          servicePage,
          myMeetingPage,
          meetingTypePage,
          meetingSettingsPage,
        ]) => {
          return {
            settings,
            integration,
            calendarPage,
            eventTypePage,
            meetingList,
            groupPage,
            servicePage,
            myMeetingPage,
            meetingTypePage,
            meetingSettingsPage,
          };
        },
      ),
    );

    const routerEndEvents$ = this.router.events.pipe(
      filter<NavigationEnd>((e) => {
        return e instanceof NavigationEnd;
      }),
    );

    this.breadcrumbsWithActions$ = combineLatest([
      routerEndEvents$.pipe(startWith({ urlAfterRedirects: typeof location !== 'undefined' ? location.href : '' })),
      links$,
      this.personalCalendarId$,
    ]).pipe(
      map(([event, links, featureTeamLinkEnabled]) => {
        const url = (typeof location !== 'undefined' ? location.href : event.urlAfterRedirects) || '';
        this.hideBackNavigation$.next(false);
        this.displayMyMeetingsActions$.next(false);
        if (url.indexOf(links.meetingList) >= 0) {
          return {
            breadcrumbs: [
              {
                url: '',
                text: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.BREADCRUMB'),
              },
            ],
            actions: [
              {
                buttonType: MeetingSchedulerNavigationButtonType.ICON_BUTTON,
                icon: 'settings',
                link: links.settings,
                navigationOption: NavigationOption.ROUTERLINK,
                labelTranslationKey: 'User settings',
              },
            ],
          };
        }

        if (url.indexOf(links.settings) >= 0) {
          return {
            breadcrumbs: [
              {
                text: this.translate.instant('MEETING_SCHEDULER.SCHEDULE_SETTINGS.BREADCRUMB_V3'),
              },
            ],
            actions: [],
          };
        }

        const isMeetingTypePage = url.indexOf(links.meetingTypePage) >= 0;
        if (isMeetingTypePage) {
          return {
            breadcrumbs: [
              {
                text: this.translate.instant('MEETING_SCHEDULER.SCHEDULE_SETTINGS.BREADCRUMB_V2'),
              },
            ],
            actions: [],
          };
        }

        const isMeetingSettingsPage = url.indexOf(links.meetingSettingsPage) >= 0;
        if (isMeetingSettingsPage) {
          return {
            breadcrumbs: [
              {
                text: this.translate.instant('MEETING_SCHEDULER.MEETING_SETTINGS.BREADCRUMB'),
              },
            ],
            actions: [],
          };
        }

        const isMyMeetingPage = url.indexOf(links.myMeetingPage) >= 0;
        if (isMyMeetingPage) {
          this.hideBackNavigation$.next(true);
          this.displayMyMeetingsActions$.next(true);
          return {
            breadcrumbs: [
              {
                url: links.myMeetingPage,
                text: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.BREADCRUMB'),
                navigationOption: NavigationOption.ROUTERLINK,
              },
            ],
            actions: [
              {
                buttonType: MeetingSchedulerNavigationButtonType.ICON_BUTTON,
                icon: 'link',
                link: links.meetingTypePage,
                navigationOption: NavigationOption.ROUTERLINK,
                labelTranslationKey: this.translate.instant('MEETING_SCHEDULER.SCHEDULE_SETTINGS.MANAGE_BOOKING_LINKS'),
              },
            ],
          };
        }

        const isCalendarPage = url.indexOf(links.calendarPage) >= 0;
        if (isCalendarPage) {
          return {
            breadcrumbs: [
              {
                url: links.meetingList,
                text: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.BREADCRUMB'),
                navigationOption: NavigationOption.ROUTERLINK,
              },
              {
                text: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.EDIT_MEETING_URL'),
              },
            ],
            actions: [],
          };
        }

        const isEventTypeSelectionPage = url.endsWith(links.eventTypePage) || url.endsWith(`${links.eventTypePage}/`);

        const isEventTypePage = links?.eventTypePage && url.includes(links.eventTypePage);
        const isGroupPage = links?.groupPage && url.includes(links.groupPage);
        const isServicePage = links?.servicePage && url.includes(links.servicePage);

        const parts = url.split('/');
        const partsLength = parts.length;

        const isEventUpdatePage =
          isEventTypePage && partsLength > 1 && parts[partsLength - 2].startsWith(CALENDAR_ID_PREFIX);
        const isGroupUpdatePage = isGroupPage && partsLength > 0 && parts[partsLength - 1].startsWith(GROUP_ID_PREFIX);
        const isServiceUpdatePage =
          isServicePage && partsLength > 0 && parts[partsLength - 1].startsWith(SERVICE_ID_PREFIX);

        if (isEventTypeSelectionPage) {
          return {
            breadcrumbs: [
              {
                url: links.meetingList,
                text: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.BREADCRUMB'),
                navigationOption: NavigationOption.ROUTERLINK,
              },
              {
                text: this.translate.instant(getBreadcrumbKey(url)),
              },
            ],
            actions: [],
          };
        }
        if (isEventTypePage && !featureTeamLinkEnabled) {
          return {
            breadcrumbs: [
              {
                url: links.meetingList,
                text: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.BREADCRUMB'),
                navigationOption: NavigationOption.ROUTERLINK,
              },
              {
                text: this.translate.instant(getBreadcrumbKey(url)),
              },
            ],
            actions: [],
          };
        }

        if (isEventTypePage && featureTeamLinkEnabled) {
          if (isEventUpdatePage) {
            return {
              breadcrumbs: [
                {
                  url: links.meetingList,
                  text: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.BREADCRUMB'),
                  navigationOption: NavigationOption.ROUTERLINK,
                },
                {
                  text: this.translate.instant(getBreadcrumbKey(url, true)),
                },
              ],
              actions: [],
            };
          }
          return {
            breadcrumbs: [
              {
                url: links.meetingList,
                text: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.BREADCRUMB'),
                navigationOption: NavigationOption.ROUTERLINK,
              },
              {
                text: this.translate.instant(getBreadcrumbKey(url)),
              },
            ],
            actions: [],
          };
        }

        if (isGroupPage && !featureTeamLinkEnabled) {
          return {
            breadcrumbs: [
              {
                url: links.meetingList,
                text: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.BREADCRUMB'),
                navigationOption: NavigationOption.ROUTERLINK,
              },
              {
                text: this.translate.instant('MEETING_SCHEDULER.BOOKING_GROUP.CREATE'),
              },
            ],
            actions: [],
          };
        }

        if (isGroupPage && featureTeamLinkEnabled) {
          if (isGroupUpdatePage) {
            return {
              breadcrumbs: [
                {
                  url: links.meetingList,
                  text: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.BREADCRUMB'),
                  navigationOption: NavigationOption.ROUTERLINK,
                },
                {
                  text: this.translate.instant('MEETING_SCHEDULER.BOOKING_GROUP.UPDATE'),
                },
              ],
              actions: [],
            };
          }
          return {
            breadcrumbs: [
              {
                url: links.meetingList,
                text: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.BREADCRUMB'),
                navigationOption: NavigationOption.ROUTERLINK,
              },
              {
                text: this.translate.instant('MEETING_SCHEDULER.BOOKING_GROUP.CREATE'),
              },
            ],
            actions: [],
          };
        }

        if (isServicePage) {
          if (isServiceUpdatePage) {
            return {
              breadcrumbs: [
                {
                  url: links.meetingList,
                  text: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.BREADCRUMB'),
                  navigationOption: NavigationOption.ROUTERLINK,
                },
                {
                  text: this.translate.instant('MEETING_SCHEDULER.BOOKING_SERVICE.UPDATE'),
                },
              ],
              actions: [],
            };
          }
          return {
            breadcrumbs: [
              {
                url: links.meetingList,
                text: this.translate.instant('MEETING_SCHEDULER.MEETING_LIST.BREADCRUMB'),
                navigationOption: NavigationOption.ROUTERLINK,
              },
              {
                text: this.translate.instant('MEETING_SCHEDULER.BOOKING_SERVICE.CREATE'),
              },
            ],
            actions: [],
          };
        }

        // links.integration is empty for the partner center
        if (url.indexOf(links.integration) >= 0) {
          return { breadcrumbs: [], actions: [] };
        }

        return null;
      }),
      shareReplay(1),
    );

    // Eager subscribe seems to be necessary here.
    this.breadcrumbsWithActions$.subscribe();

    this.breadcrumbs$ = this.breadcrumbsWithActions$.pipe(map((ba) => (ba ? ba.breadcrumbs : null)));
    this.actions$ = this.breadcrumbsWithActions$.pipe(map((ba) => (ba ? ba.actions : null)));
  }
}
